import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import type { ReconciliationRepository } from './repository';
import type {
  ReconciliationException,
  ReconciliationExceptionQuery,
  ReconciliationRunQuery,
} from './types';

type RunListResponse = ApiResponse<'/api/v1/reconciliation-runs', 'get'>;
type RunResponse = ApiResponse<'/api/v1/reconciliation-runs/{id}', 'get'>;
type CreateRunResponse = ApiResponse<'/api/v1/reconciliation-runs', 'post'>;
type ExceptionListResponse = ApiResponse<'/api/v1/reconciliation-exceptions', 'get'>;
type ExceptionResponse = ApiResponse<'/api/v1/reconciliation-exceptions/{id}', 'get'>;
type ResolveResponse = ApiResponse<'/api/v1/reconciliation-exceptions/{id}/resolve', 'post'>;
type AcknowledgeResponse = ApiResponse<
  '/api/v1/reconciliation-exceptions/{id}/acknowledge',
  'post'
>;

export function createRealReconciliationRepository(
  client: ApiClient,
  accessToken: string | null,
): ReconciliationRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async listRuns(query, signal) {
      const response = await client.get<RunListResponse>('/api/v1/reconciliation-runs', {
        ...auth,
        query: runQuery(query),
        signal,
      });
      return page(response);
    },
    async createRun(input, idempotencyKey) {
      const response = await client.post<CreateRunResponse>(
        '/api/v1/reconciliation-runs',
        {
          type: input.type,
          ...(input.partnerId === undefined || input.partnerId.trim().length === 0
            ? {}
            : { partnerId: input.partnerId.trim() }),
          reason: input.reason.trim(),
        },
        { ...auth, idempotencyKey: requireUuidV4(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
    async getRun(runId, signal) {
      const response = await client.get<RunResponse>(runPath(runId), { ...auth, signal });
      return response.data.data;
    },
    async listExceptions(query, signal) {
      const response = await client.get<ExceptionListResponse>(
        '/api/v1/reconciliation-exceptions',
        { ...auth, query: exceptionQuery(query), signal },
      );
      return { ...page(response), items: response.data.data.map(sanitizeException) };
    },
    async getException(exceptionId, signal) {
      const response = await client.get<ExceptionResponse>(exceptionPath(exceptionId), {
        ...auth,
        signal,
      });
      return sanitizeException(response.data.data);
    },
    async resolveException(exceptionId, input, idempotencyKey) {
      requireUuidV4(idempotencyKey);
      const response = await client.post<ResolveResponse>(
        `${exceptionPath(exceptionId)}/resolve`,
        { reason: input.reason.trim() },
        auth,
      );
      return { data: sanitizeException(response.data.data), requestId: response.requestId };
    },
    async acknowledgeException(exceptionId, input, idempotencyKey) {
      requireUuidV4(idempotencyKey);
      const response = await client.post<AcknowledgeResponse>(
        `${exceptionPath(exceptionId)}/acknowledge`,
        { reason: input.reason.trim(), evidenceReference: input.evidenceReference.trim() },
        auth,
      );
      return { data: sanitizeException(response.data.data), requestId: response.requestId };
    },
  };
}

function runQuery(query: ReconciliationRunQuery) {
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    type: query.filters.type,
    trigger: query.filters.trigger,
    phase: query.filters.phase,
    partnerId: query.filters.partnerId.trim(),
    requestedFrom: bangkokWallTimeToRfc3339(query.filters.requestedFrom),
    requestedTo: bangkokWallTimeToRfc3339(query.filters.requestedTo),
  });
}

function exceptionQuery(query: ReconciliationExceptionQuery) {
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    status: query.filters.status,
    severity: query.filters.severity,
    type: query.filters.type.trim().toUpperCase(),
    entityType: query.filters.entityType.trim().toUpperCase(),
    entityId: query.filters.entityId.trim(),
    partnerId: query.filters.partnerId.trim(),
    detectedFrom: bangkokWallTimeToRfc3339(query.filters.detectedFrom),
    detectedTo: bangkokWallTimeToRfc3339(query.filters.detectedTo),
  });
}

function page<Item>(response: {
  readonly data: {
    readonly data: readonly Item[];
    readonly pagination: { readonly nextCursor: string | null };
  };
  readonly requestId: string | null;
}) {
  return {
    items: response.data.data,
    nextCursor: response.data.pagination.nextCursor,
    requestId: response.requestId,
  };
}

const safeDiagnosticKeys = new Set([
  'amount',
  'currency',
  'sourceTotal',
  'internalTotal',
  'difference',
  'differenceCurrency',
  'duplicateIds',
  'missingReceipts',
  'staleReservations',
  'evidence',
]);

function sanitizeException(value: ReconciliationException): ReconciliationException {
  return {
    ...value,
    expected: sanitizeDiagnostics(value.expected),
    actual: sanitizeDiagnostics(value.actual),
  };
}

function sanitizeDiagnostics(value: Readonly<Record<string, unknown>> | null) {
  if (value === null) return null;
  return Object.fromEntries(
    Object.entries(value).filter(([key, item]) => safeDiagnosticKeys.has(key) && safeValue(item)),
  );
}

function safeValue(value: unknown): boolean {
  return (
    typeof value === 'string' ||
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  );
}

function compact(values: Readonly<Record<string, unknown>>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

function runPath(runId: string) {
  return `/api/v1/reconciliation-runs/${encodeURIComponent(runId)}`;
}
function exceptionPath(exceptionId: string) {
  return `/api/v1/reconciliation-exceptions/${encodeURIComponent(exceptionId)}`;
}

function requireUuidV4(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value))
    throw new Error('A UUID v4 idempotency key is required');
  return value;
}
